import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LuCheck, LuListFilter, LuX } from 'react-icons/lu'

import { Organization } from '@/feature/observatory'


import styles from '../ExecutionBoardFilters/ExecutionBoardFilters.module.css'
import { OpeningFilterPopout } from '../ExecutionBoardFilters/ExecutionBoardFilters'

export const AssigneeFilterPopout = ({
  setFilteringAssignee,
  availableAssignees,
  currentSelectedOrgId,
  openingFilterPopout,
  onOpenFilterPopout,
}: {
  setFilteringAssignee: (orgId: number | null) => void
  availableAssignees: Organization[]
  currentSelectedOrgId?: number
  openingFilterPopout: OpeningFilterPopout | null
  onOpenFilterPopout: (popout: OpeningFilterPopout) => void
}) => {
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const filterButtonRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)

  const [popoverPosition, setPopoverPosition] = useState(
    filterButtonRef.current?.getBoundingClientRect() ?? { bottom: 0, right: 0 },
  )

  const showPopover = (e: React.MouseEvent) => {
    e.stopPropagation()
    const pos = filterButtonRef.current?.getBoundingClientRect()
    if (pos) {
      setPopoverPosition(pos)
    }
    dialogRef?.current?.showPopover()
    onOpenFilterPopout(OpeningFilterPopout.ASSIGNED_TO)
  }

  const { bottom, right } = popoverPosition

  const handleClose = useCallback(
    (applyClose: boolean = false) => {
      if (dialogRef.current) {
        if (!applyClose && currentSelectedOrgId !== selectedOrgId) {
          setSelectedOrgId(currentSelectedOrgId ?? null)
        }
        dialogRef.current.hidePopover()
      }
    },
    [currentSelectedOrgId, selectedOrgId],
  )

  const handleSelectAssignee = useCallback(
    (orgId: number) => {
      if (orgId === selectedOrgId) {
        setSelectedOrgId(null)
      } else {
        setSelectedOrgId(orgId)
      }
    },
    [selectedOrgId],
  )

  const handleApply = useCallback(() => {
    setFilteringAssignee(selectedOrgId)
    handleClose(true)
  }, [selectedOrgId, setFilteringAssignee, handleClose])

  const handleClear = useCallback(() => {
    setSelectedOrgId(null)
    setFilteringAssignee(null)
  }, [setFilteringAssignee])

  useEffect(() => {
    if (openingFilterPopout !== OpeningFilterPopout.ASSIGNED_TO) {
      handleClose()
    }
  }, [openingFilterPopout])

  return (
    <div>
      <div
        ref={filterButtonRef}
        className={styles.button}
        data-testid="execution-board-button"
        onClick={showPopover}
      >
        <LuListFilter size="16" />
        Assigned To
      </div>

      <dialog
        ref={dialogRef}
        className={styles.orgPopoutContainer}
        style={{ top: bottom + 10, left: right - 80 }}
        popover="manual"
      >
        <div className={styles.popoutHeader}>
          <div className={styles.headerActions}>
            <button
              className={styles.actionButton}
              onClick={() => {
                handleClear()
                handleClose(true)
              }}
            >
              <LuX size={16} />
            </button>
          </div>
        </div>
        <div className={styles.radioWrapper}>
          {availableAssignees.map((org: Organization) => (
            <label key={`option-org-${org.id}`} className={styles.basicRadio}>
              <input
                type="radio"
                value={org.id}
                checked={org.id === selectedOrgId}
                onChange={() => {}}
                onClick={() => handleSelectAssignee(org.id)}
              />
              {org.name}
              {org.id === selectedOrgId && <LuCheck size="24" className={styles.check} strokeWidth={1.5} />}
            </label>
          ))}
        </div>
        <div className={styles.footerActions}>
          <button className={styles.clearAllButton} onClick={handleClear}>
            Clear all
          </button>
          <button className={styles.applyButton} onClick={handleApply}>
            Apply
          </button>
        </div>
      </dialog>
    </div>
  )
}
